import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Mobile disclosure wrapper (Spec 7.3, owner feedback 2026-07-26).
 *
 * The dashboard is a 3-column desktop layout (Watchlist | Desk | Positions). Stacking
 * those on a phone buries the main panel below the side rails, so you scroll past the
 * Watchlist to reach the thing you opened the app for.
 *
 * Below `md` this collapses its children behind a 44px header row (icon + title + count
 * + chevron), closed by default. At `md`+ the header disappears and the content is always
 * open — the desktop layout is byte-identical to before.
 *
 * The `count` badge keeps information scent while collapsed: you can see there are 6
 * symbols / 4 open positions without expanding.
 */
interface CollapsiblePanelProps {
  title: string;
  icon: React.ElementType;
  count?: number;
  /** Wrapper classes (grid placement/order) applied at every width. */
  className?: string;
  children: React.ReactNode;
}

const CollapsiblePanel: React.FC<CollapsiblePanelProps> = ({
  title, icon: Icon, count, className = '', children,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden ${className}`}
      data-testid={`panel-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="md:hidden w-full min-h-[44px] flex items-center justify-between gap-2 px-3 py-2
                   text-slate-300 touch-manipulation hover:bg-slate-700/40 transition-colors"
      >
        <span className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 shrink-0 text-slate-400" aria-hidden="true" />
          <span className="text-sm font-medium truncate">{title}</span>
          {typeof count === 'number' && (
            <span className="text-10 font-semibold px-1.5 py-0.5 rounded-full bg-slate-700/70 text-slate-300 shrink-0">
              {count}
            </span>
          )}
        </span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {/* Collapsed on mobile until tapped; always rendered from md up. */}
      <div className={`${open ? 'block' : 'hidden'} md:block`}>{children}</div>
    </div>
  );
};

export default CollapsiblePanel;
